import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { authUtils } from '../../utils/auth';
import { chatStorageUtils } from '../../utils/chatStorage';
import { API_ENDPOINTS } from '../../config/api';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatMenu from './ChatMenu';
import WelcomeMessage, { getRandomWelcomeMessage } from './WelcomeMessage';
import LoadingMessage from './LoadingMessage';
import { ConversationHistoryItem, historyService } from '../../services/historyService';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'system';
  timestamp: Date;
  attachment?: File;
  isWelcome?: boolean;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { userName } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    if (!authUtils.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Check if we need to start a new chat (from New Chat button on another page)
    const startNewChat = sessionStorage.getItem('startNewChat');
    if (startNewChat) {
      sessionStorage.removeItem('startNewChat'); // Clear the flag

      // Clear all previous session storage to start fresh
      chatStorageUtils.clearAllSessions();

      // Generate new session ID
      const session = authUtils.generateSessionId();
      setSessionId(session);
      authUtils.setSession(session);

      // Start fresh with welcome message
      const welcomeMessage: Message = {
        id: 'welcome-' + Date.now(),
        content: getRandomWelcomeMessage(userName || 'there'),
        role: 'system',
        timestamp: new Date(),
        isWelcome: true,
      };
      setMessages([welcomeMessage]);
      chatStorageUtils.saveMessages(session, [welcomeMessage]);
      chatStorageUtils.setActiveSession(session);
      return; // Exit early
    }

    // Check if there's a selected conversation to load from navigation
    const selectedConversationData = sessionStorage.getItem('selectedConversation');
    if (selectedConversationData) {
      try {
        const selectedConversation: ConversationHistoryItem = JSON.parse(selectedConversationData);
        sessionStorage.removeItem('selectedConversation'); // Clear it after use
        handleSelectConversation(selectedConversation);
        return; // Don't continue with normal initialization
      } catch (error) {
        console.error('Failed to parse selected conversation:', error);
        sessionStorage.removeItem('selectedConversation');
      }
    }

    // Initialize session
    let session = authUtils.getSession();
    if (!session) {
      session = authUtils.generateSessionId();
      authUtils.setSession(session);
    }
    setSessionId(session);

    // Try to restore messages from session storage first
    const cachedMessages = chatStorageUtils.loadMessages(session);

    if (cachedMessages && cachedMessages.length > 0) {
      // Restore messages from cache
      console.log('Restoring messages from session storage for session:', session);
      setMessages(cachedMessages);
      chatStorageUtils.setActiveSession(session);
    } else {
      // Add welcome message only if not loading a conversation and no cached messages
      if (!loadingConversation) {
        const welcomeMessage: Message = {
          id: 'welcome',
          content: getRandomWelcomeMessage(userName || 'there'),
          role: 'system',
          timestamp: new Date(),
          isWelcome: true,
        };
        setMessages([welcomeMessage]);
        // Save the welcome message to session storage
        chatStorageUtils.saveMessages(session, [welcomeMessage]);
      }
    }
  }, [userName, navigate, loadingConversation]);

  useEffect(() => {
    scrollToBottom();

    // Save messages to session storage whenever they change
    // Don't save if there are no messages or if we're loading a conversation
    if (messages.length > 0 && sessionId && !loadingConversation) {
      chatStorageUtils.saveMessages(sessionId, messages);
    }
  }, [messages, sessionId, loadingConversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectConversation = (conversation: ConversationHistoryItem) => {
    setLoadingConversation(true);

    // Clear current messages
    setMessages([]);

    // Load messages from the selected conversation
    const loadedMessages: Message[] = conversation.messages.map((msg) => ({
      id: msg.id,
      content: msg.content,
      role: msg.role === 'assistant' ? 'system' : msg.role as 'user' | 'system',
      timestamp: new Date(msg.timestamp),
    }));

    // Update session ID to resume the conversation
    setSessionId(conversation.session_id);
    authUtils.setSession(conversation.session_id);

    // Save the loaded conversation to session storage
    chatStorageUtils.saveMessages(conversation.session_id, loadedMessages);
    chatStorageUtils.setActiveSession(conversation.session_id);

    // Set the loaded messages
    setMessages(loadedMessages);
    setLoadingConversation(false);

    // Scroll to bottom after loading
    setTimeout(scrollToBottom, 100);
  };

  const handleNewChat = () => {
    // Clear the flag if it exists (set by New Chat button)
    sessionStorage.removeItem('startNewChat');

    // Clear all previous session storage to start fresh
    chatStorageUtils.clearAllSessions();

    // Generate new session ID
    const newSession = authUtils.generateSessionId();
    setSessionId(newSession);
    authUtils.setSession(newSession);

    // Add welcome message with typing effect
    const welcomeMessage: Message = {
      id: 'welcome-' + Date.now(),
      content: getRandomWelcomeMessage(userName || 'there'),
      role: 'system',
      timestamp: new Date(),
      isWelcome: true,
    };
    setMessages([welcomeMessage]);

    // Save the new chat to session storage
    chatStorageUtils.saveMessages(newSession, [welcomeMessage]);
    chatStorageUtils.setActiveSession(newSession);

    // Scroll to bottom
    setTimeout(scrollToBottom, 100);
  };

  const handleSendMessage = async (content: string, attachment?: File) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date(),
      attachment,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setLoading(true);

    // Check if this is the first user message (excluding welcome message)
    const userMessagesCount = messages.filter(msg => msg.role === 'user').length;
    const isFirstMessage = userMessagesCount === 0;

    // Immediately add or update conversation in history
    // Only add if there are actual messages (not just welcome)
    if (isFirstMessage) {
      const conversation = historyService.createConversationFromMessages(
        sessionId,
        updatedMessages
      );
      // Only add to history if it has actual content
      if (conversation.messages.length > 0) {
        historyService.addOrUpdateConversationLocally(conversation);
        // Trigger history refresh in ChatMenu
        setHistoryRefreshKey(prev => prev + 1);
      }
    }

    try {
      let requestData: any;
      let headers: any = {};

      if (attachment) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('message', content);
        formData.append('session_id', sessionId);
        formData.append('file', attachment);

        requestData = formData;
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        // Use JSON for text-only messages
        requestData = {
          message: content,
          session_id: sessionId,
        };
        headers['Content-Type'] = 'application/json';
      }

      const response = await axios.post(API_ENDPOINTS.CHAT.PROCESS, requestData, {
        headers,
        timeout: 180000, // 3 minutes timeout (180 seconds)
      });

      if (response.data.response) {
        const systemMessage: Message = {
          id: Date.now().toString(),
          content: response.data.response,
          role: 'system',
          timestamp: new Date(),
        };
        const finalMessages = [...updatedMessages, systemMessage];
        setMessages(finalMessages);

        // Update conversation history with the response
        const conversation = historyService.createConversationFromMessages(
          sessionId,
          finalMessages
        );
        // Only add to history if it has actual content
        if (conversation.messages.length > 0) {
          historyService.addOrUpdateConversationLocally(conversation);
          // Trigger history refresh in ChatMenu
          setHistoryRefreshKey(prev => prev + 1);
        }
      }
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        const errorMessage: Message = {
          id: Date.now().toString(),
          content: 'The system is currently overloaded. Please try again later.',
          role: 'system',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-jobesta-gray-20">
      <ChatMenu
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        currentSessionId={sessionId}
        refreshKey={historyRefreshKey}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 pt-16 md:pt-4 space-y-4">
          {messages.map((message) => (
            message.isWelcome ? (
              <WelcomeMessage
                key={message.id}
                userName={userName || 'there'}
                message={message.content}
                timestamp={message.timestamp}
              />
            ) : (
              <ChatMessage key={message.id} message={message} />
            )
          ))}
          {loading && <LoadingMessage />}
          <div ref={messagesEndRef} />
        </div>
        <div className="flex-shrink-0">
          <ChatInput onSendMessage={handleSendMessage} disabled={loading} />
        </div>
      </div>
    </div>
  );
};

export default Chat;